"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import Link from "next/link"
import { Button, Input, Card } from "@repo/ui"
import { ErrorDisplay } from "@/components/ErrorDisplay"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { ArrowLeft, Clock } from "lucide-react"
import { createClient as createSupabaseClient } from "@supabase/supabase-js"
import { logSystemAudit } from "@/app/actions/auth"
import { AppError } from '@repo/errors'
import { parseError, ERROR_MESSAGES, TherapistAppError, createErrorFromMessage, enrichError } from '@/lib/utils/error-migration-helper'

export default function ForgotPasswordPage() {
  return (
    <ErrorBoundary>
      <ForgotPasswordContent />
    </ErrorBoundary>
  )
}

function ForgotPasswordContent() {
  const [email, setEmail] = useState("")
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<TherapistAppError | null>(null)
  const [retryTimer, setRetryTimer] = useState<number>(0)

  const clearError = () => {
    setError(null)
  }

  // Handle retry timer countdown
  useEffect(() => {
    if (retryTimer > 0) {
      const interval = setInterval(() => {
        setRetryTimer(prev => prev - 1)
      }, 1000)
      return () => clearInterval(interval)
    }
  }, [retryTimer])

  // Email validation regex
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    // Check if rate limited
    if (retryTimer > 0) {
      setError(enrichError(
        new AppError(`Please wait ${retryTimer} seconds before trying again`, {
          code: 'RATE_LIMITED',
          statusCode: 429
        }),
        { severity: 'info', category: 'validation' }
      ))
      return
    }

    // Validate email
    if (!email.trim()) {
      setError(enrichError(
        new AppError("Please enter your email address", {
          code: 'VALIDATION_ERROR',
          statusCode: 400
        }),
        { severity: 'info', category: 'validation' }
      ))
      return
    }

    if (!validateEmail(email)) {
      setError(enrichError(
        new AppError("Please enter a valid email address", {
          code: 'VALIDATION_ERROR',
          statusCode: 400
        }),
        { severity: 'info', category: 'validation' }
      ))
      return
    }

    setIsLoading(true)

    try {
      // Dedicated implicit-flow client. `@supabase/ssr`'s createServerClient
      // force-overrides flowType to "pkce" AND its server storage only
      // flushes to cookies on auth events (SIGNED_IN/TOKEN_REFRESHED/etc),
      // so resetPasswordForEmail's code_verifier never makes it into a
      // cookie — exchange later fails with bad_code_verifier. A direct
      // implicit client sidesteps PKCE entirely: Supabase returns tokens
      // in the email link's URL hash and /reset-password consumes them.
      const supabase = createSupabaseClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL!,
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
        {
          auth: {
            flowType: 'implicit',
            persistSession: false,
            autoRefreshToken: false,
            detectSessionInUrl: false,
          },
        }
      )

      const isLocalhost =
        typeof window !== 'undefined' && window.location.host.includes('localhost')
      const baseUrl = isLocalhost
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_THERAPIST_APP_URL ||
            process.env.NEXT_PUBLIC_APP_URL ||
            window.location.origin)

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        // Point directly at /reset-password — bypassing /auth/callback. The
        // implicit-flow tokens live in the URL hash which is browser-only,
        // so the server-side callback couldn't read them anyway.
        redirectTo: `${baseUrl}/reset-password`,
      })

      if (resetError) {
        console.warn('[ForgotPassword] reset error:', resetError)
      }

      // Log password reset attempt for HIPAA compliance (non-blocking)
      logSystemAudit({
        action: 'auth.password_reset_request',
        resource: 'auth',
        details: {
          email,
          // Always log as success — we surface success to the user either
          // way to prevent email enumeration.
          success: true,
        },
      }).catch((err: unknown) => {
        console.error('[ForgotPassword] Failed to log password reset:', err)
      })

      // Email-enumeration protection: claim success regardless of whether
      // the email exists in our system.
      setIsSubmitted(true)
    } catch (err) {
      const parsedError = parseError(err)
      setError(parsedError)

      // If rate limited, set retry timer
      if (parsedError.message.includes("Too many attempts")) {
        setRetryTimer(60) // 60 second cooldown
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mt-8">
            <Image
              src="/alli_logo.png"
              alt="Alli"
              width={280}
              height={112}
              priority
              className="h-28 w-auto"
            />
          </div>
          <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">
            Check your email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent password reset instructions to {email}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <Card variant="elevated" className="px-4 py-8 sm:px-10 bg-white/80 backdrop-blur-sm">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                If you don't see the email, check your spam folder.
              </p>
              <Link
                href="/login"
                className="font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                Return to sign in
              </Link>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mt-8">
          <Image
            src="/alli_logo.png"
            alt="Alli"
            width={280}
            height={112}
            priority
            className="h-28 w-auto"
          />
        </div>
        <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your email and we'll send you instructions
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card variant="elevated" className="px-4 py-8 sm:px-10 bg-white/80 backdrop-blur-sm">
          {error && (
            <div className="mb-4">
              <ErrorDisplay 
                error={error} 
                onDismiss={clearError} 
              />
            </div>
          )}

          {retryTimer > 0 && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                <p className="text-sm text-yellow-800">
                  Please wait {retryTimer} seconds before trying again
                </p>
              </div>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <Input
              label="Email address"
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />

            <div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full transition-all hover:shadow-lg"
                isLoading={isLoading}
                disabled={isLoading || retryTimer > 0}
              >
                {isLoading ? "Sending..." : 
                 retryTimer > 0 ? `Wait ${retryTimer}s` : 
                 "Send reset instructions"}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link
              href="/login"
              className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to sign in
            </Link>
          </div>
        </Card>
        
      </div>
    </div>
  )
}